import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import Axios from "axios";
import { toast } from "react-toastify";
import { API_ROUTE, Routes } from "../../Helpers/Constants";
import AuthenticationContext from "../../Store/Authentication-Context";
import { NavBar } from "../NavBar";
import * as BillingSyncApi from "../../Helpers/BillingSyncApi";
import PanelState from "../MainComponents/PanelState";
import ResellerPicker, {
    ResellerPickerUnavailable,
    ChooseResellerPrompt,
} from "../BillingSyncSteps/ResellerPicker";
import Tally from "../MainComponents/Tally";
import {
    describeState,
    describeResolution,
    describeNextAttempt,
    describeAdditionType,
} from "../../Helpers/BillingSyncLabels";

/**
 * Task 37, B6 — where each billing line has got to, and how to put right anything that failed.
 *
 * SEPARATE FROM THE SETUP PAGE deliberately. Setup is done once; this is the page a reseller returns
 * to during the month, and burying it behind a tab on a settings screen would put the operational
 * view inside the configuration one.
 *
 * Shaped on Pages/ConnectWiseSync.js — the mount-time token check, the loading and empty renders,
 * toast for feedback.
 */

/** How often to look for a background retry that is still running. */
const RETRY_POLL_MS = 3000;

/**
 * Master administrators may look at any reseller; everyone else only at their own.
 *
 * Identical to ConnectWiseSync.js's copy, and for the same reason: a cheap pre-filter that decides
 * whether the reseller list is worth asking for. The API decides the reseller from the signed token
 * and confirms entitlement server-side, so a browser claiming otherwise gains nothing.
 */
function isMasterAdministrator() {
    try {
        return sessionStorage.getItem("ResellerID") === "1";
    } catch (e) {
        return false;
    }
}

export default function ConnectWiseSyncStatus() {
    const authContext = useContext(AuthenticationContext);
    const { setAuthContext } = authContext;

    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [retryState, setRetryState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [retryingLine, setRetryingLine] = useState(null);

    // A refresh that keeps the figures on screen, as distinct from the first paint that has none.
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const hasLoadedOnce = useRef(false);

    // Acknowledged lines are held back by the server unless asked for. The toggle is what makes
    // acknowledging reversible in practice: the decision is one-way, but nothing is hidden for good.
    const [showAcknowledged, setShowAcknowledged] = useState(false);
    const [acknowledging, setAcknowledging] = useState(null);

    // This page used to pass no reseller at all, so a master administrator who had selected a
    // reseller on the setup screen still saw their OWN status here — the two halves of the same
    // journey disagreed about whose billing was on screen. It now scopes the same way the setup
    // page does. Retry stays refused server-side when acting on behalf; see the service's Finish().
    const [resellers, setResellers] = useState([]);
    const [selectedReseller, setSelectedReseller] = useState(null);
    const [canChooseReseller, setCanChooseReseller] = useState(false);
    // Same as ConnectWiseSync.js: say so when the server would not confirm the master account.
    const [pickerUnavailable, setPickerUnavailable] = useState(false);

    // N13 — nothing loads for a master administrator until a reseller is chosen; see ConnectWiseSync.js.
    const awaitingChoice = canChooseReseller && !selectedReseller;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = await setAuthContext(new Date());
                const orgId = sessionStorage.getItem("orgId");
                const response = await Axios.get(`${API_ROUTE}${Routes.VALIDATE_TOKEN}/${orgId}`, {
                    headers: {
                        Authorisation: `Bearer ${token}`,
                        CloudCoverToken: sessionStorage.getItem("CloudCoverToken"),
                    },
                });
                if (!cancelled && !JSON.parse(response.data)) window.location.href = "/login";
            } catch (err) {
                if (err?.response?.status === 440) window.location.href = "/TimeoutErrorHandler";
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [setAuthContext]);

    // THE SERVER DECIDES WHETHER THIS PICKER EXISTS, not sessionStorage. Same reasoning, and the
    // same 401/403-is-an-answer handling, as ConnectWiseSync.js.
    useEffect(() => {
        if (!isMasterAdministrator()) return;
        let cancelled = false;
        (async () => {
            try {
                // The server's own test, asked before the picker is offered — see ConnectWiseSync.js
                // for why the reseller list alone is not a sufficient answer.
                const scope = await BillingSyncApi.getScope(setAuthContext);
                if (cancelled) return;
                if (!scope || !scope.canActOnBehalf) {
                    setResellers([]);
                    setCanChooseReseller(false);
                    setPickerUnavailable(true);
                    return;
                }

                const result = await BillingSyncApi.getResellers(setAuthContext);
                const list = Array.isArray(result) ? result : [];
                if (cancelled) return;
                setResellers(list);
                setCanChooseReseller(list.length > 0);
            } catch (err) {
                if (cancelled) return;
                setResellers([]);
                setCanChooseReseller(false);
                const status = err?.response?.status;
                if (status === 401 || status === 403) {
                    setPickerUnavailable(true);
                    return;
                }
                // Covers both the probe and the list — see ConnectWiseSync.js.
                const message = BillingSyncApi.handleApiError(
                    err, "Could not check which resellers you can work on.");
                if (message) toast.error(message, { toastId: "cws-resellers" });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [setAuthContext]);

    const load = useCallback(async () => {
        // REFRESHING IS NOT LOADING. `loading` is first paint only and blanks the screen; a refresh
        // has to leave the figures where they are while it happens, which is what the client asked
        // for — the whole screen must not flicker or reset. The Refresh button also had no feedback
        // at all before this, because `loading` was already false by the time it could be pressed.
        setRefreshing(true);
        setError(null);
        try {
            const [lines, retry] = await Promise.all([
                BillingSyncApi.getSyncStatus(setAuthContext, selectedReseller, showAcknowledged),
                BillingSyncApi.getRetryState(setAuthContext, selectedReseller),
            ]);
            setStatus(lines);
            setRetryState(retry);
            hasLoadedOnce.current = true;
        } catch (err) {
            const message = BillingSyncApi.handleApiError(err, "Could not load your sync status.");
            if (message) {
                // Only when there is nothing already on screen. This is also the poll target while
                // a retry runs, and a single failed tick must not replace a table the reseller is
                // reading with an error panel.
                //
                // A ref rather than reading `status`, which would put it in the dependency array
                // below, give `load` a new identity after every successful load, and re-fire the
                // effect that calls it — a loop.
                if (!hasLoadedOnce.current) setError(message);
                toast.error(message, { toastId: "cws-status" });
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [setAuthContext, selectedReseller, showAcknowledged]);

    /**
     * A DIFFERENT RESELLER MEANS DIFFERENT BILLING. Same defect, same fix as ConnectWiseSync.js:
     * `load` left `status` alone, so the previous reseller's lines and tallies stayed on the screen
     * under the new reseller's name until the fetch returned.
     *
     * Declared before the effect that calls load, so the clear commits first and the panel shows a
     * loader rather than the old table.
     *
     * `loading` rather than `refreshing`, because this IS a first paint for the reseller now being
     * shown. REFRESH IS DELIBERATELY NOT TOUCHED — it must leave the figures where they are, which
     * is what the client asked for and what the loading/refreshing split above exists to deliver.
     * Only a change of reseller clears.
     *
     * hasLoadedOnce resets with it. It suppresses in-panel errors to protect a table somebody is
     * reading, but there is no longer a table to protect, and a failure against the new reseller
     * has to be able to say so instead of leaving an empty screen behind.
     */
    const lastReseller = useRef(selectedReseller);
    useEffect(() => {
        if (lastReseller.current === selectedReseller) return;
        lastReseller.current = selectedReseller;

        setStatus(null);
        setRetryState(null);
        setError(null);
        hasLoadedOnce.current = false;
        setLoading(true);
    }, [selectedReseller]);

    useEffect(() => {
        if (awaitingChoice) return;
        load();
    }, [load, awaitingChoice]);

    // Only while a retry is actually running, and it stops of its own accord: the server bounds how
    // long it will claim one is, so this cannot poll for ever.
    useEffect(() => {
        if (!retryState || !retryState.isRetrying) return undefined;
        const timer = setTimeout(load, RETRY_POLL_MS);
        return () => clearTimeout(timer);
    }, [retryState, load]);

    async function handleRetryAll() {
        setBusy(true);
        try {
            const result = await BillingSyncApi.retryAll(setAuthContext, selectedReseller);
            if (result && result.message) toast.info(result.message);
            setRetryState((current) => ({ ...(current || {}), isRetrying: true }));
            await load();
        } catch (err) {
            const message = BillingSyncApi.handleApiError(err, "The retry could not be started.");
            if (message) toast.error(message, { toastId: "cws-retry-all" });
            await load();
        } finally {
            setBusy(false);
        }
    }

    async function handleRetryLine(itemId) {
        setRetryingLine(itemId);
        try {
            const result = await BillingSyncApi.retryLine(setAuthContext, itemId, selectedReseller);
            if (result && result.message) toast.success(result.message);
            await load();
        } catch (err) {
            const message = BillingSyncApi.handleApiError(err, "That line could not be retried.");
            if (message) toast.error(message, { toastId: "cws-retry-line" });
            await load();
        } finally {
            setRetryingLine(null);
        }
    }

    /**
     * Takes a failed line out of the active view.
     *
     * The confirmation says what this does NOT do. "Acknowledge" is easily read as "cancel", and a
     * line only reaches this point by failing — a failure may have reached ConnectWise in part, so
     * claiming the charge is stopped would be worse than the problem being solved.
     */
    async function handleAcknowledge(itemId) {
        const confirmed = window.confirm(
            "Hide this failed line?\n\n"
            + "It stops appearing in the list and stops counting as a failure. It does NOT cancel "
            + "anything already sent to ConnectWise, and it cannot be undone — though you can still "
            + "see it with 'Show acknowledged'.");

        if (!confirmed) return;

        setAcknowledging(itemId);
        try {
            const result = await BillingSyncApi.acknowledgeLines(
                setAuthContext, [itemId], selectedReseller);
            if (result && result.message) toast.success(result.message);
            await load();
        } catch (err) {
            const message = BillingSyncApi.handleApiError(err, "That line could not be acknowledged.");
            if (message) toast.error(message, { toastId: "cws-acknowledge" });
            await load();
        } finally {
            setAcknowledging(null);
        }
    }

    const lines = (status && status.lines) || [];
    const retrying = busy || (retryState && retryState.isRetrying);
    const acknowledgedCount = (status && status.acknowledgedCount) || 0;

    // billing-sync-page bounds this screen to the window so its content can scroll; see general.css
    // and ConnectWiseSync.js.
    return (
        <div className="billing-sync-page">
            <NavBar page="connectWiseSyncStatus" authContext={authContext} />
            {/* Same header as the partner Organisations page (Reseller.js); see ConnectWiseSync.js. */}
            <div className="card-header reseller-card p-1">
                <span className="SectionTitle">CONNECTWISE SYNC STATUS</span>
            </div>
            <div className="billing-sync-content" style={{ padding: 20 }}>
                {canChooseReseller && (
                    <ResellerPicker
                        inputId="cws-reseller"
                        resellers={resellers}
                        ownId={sessionStorage.getItem("ResellerID")}
                        value={selectedReseller}
                        onChange={setSelectedReseller}
                        hint="You can see any reseller's billing. Retrying it remains theirs to do."
                    />
                )}
                {pickerUnavailable && <ResellerPickerUnavailable />}

                {awaitingChoice ? (
                    <ChooseResellerPrompt detail="Select a reseller above to see its billing sync status." />
                ) : (
                <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    {/* Only offered when something is actually hidden — an empty checkbox would
                        raise a question the reseller has no reason to be asked. */}
                    <div>
                        {acknowledgedCount > 0 && (
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="cws-show-acknowledged"
                                    checked={showAcknowledged}
                                    onChange={(e) => setShowAcknowledged(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="cws-show-acknowledged">
                                    Show acknowledged ({acknowledgedCount})
                                </label>
                            </div>
                        )}
                    </div>
                    <div>
                        <button
                            type="button"
                            className="btn btn-primary me-2"
                            disabled={loading || refreshing || retrying}
                            onClick={() => load()}
                        >
                            {refreshing && !loading ? "Refreshing…" : "Refresh"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={loading || retrying || !(status && status.canRetry)}
                            onClick={handleRetryAll}
                            title={(status && status.retryBlockedReason) || undefined}
                        >
                            {retrying ? "Retrying…" : "Retry everything that failed"}
                        </button>
                    </div>
                </div>

                {retryState && retryState.failureReason && (
                    <div className="alert alert-danger py-2" style={{ fontSize: 14 }}>
                        The last retry did not finish: {retryState.failureReason}
                    </div>
                )}

                {status && status.retryBlockedReason && status.failedCount > 0 && (
                    <div className="alert alert-info py-2" style={{ fontSize: 14 }}>
                        {status.retryBlockedReason}
                    </div>
                )}

                {loading || (lines.length === 0) ? (
                    // One block, three outcomes. A failed first load used to land here as "Nothing
                    // has been billed yet." — the most reassuring possible way to report an outage.
                    <PanelState
                        busy={loading}
                        error={error}
                        title="Nothing has been billed yet."
                        detail="Lines appear here once a month's billing has been calculated and sync is on."
                        actionLabel={error ? "Try again" : undefined}
                        busyLabel="Trying…"
                        onAction={error ? () => load() : undefined}
                    />
                ) : (
                    <>
                        <div className="d-flex gap-3 mb-3 flex-wrap">
                            <Tally label="Sent" value={status.syncedCount} tone="success" />
                            <Tally label="Waiting" value={status.waitingCount} />
                            <Tally label="Sending" value={status.runningCount} tone="info" />
                            <Tally
                                label="Need attention"
                                value={status.failedCount}
                                tone={status.failedCount > 0 ? "danger" : null}
                            />
                        </div>

                        <table className="table table-sm align-middle">
                            <thead>
                                <tr>
                                    <th>Customer</th>
                                    <th>Type</th>
                                    <th>Period</th>
                                    <th>State</th>
                                    <th>What to do</th>
                                    <th style={{ width: 210 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((line) => (
                                    <StatusRow
                                        key={line.billingSyncItemId}
                                        line={line}
                                        disabled={retrying || retryingLine != null || acknowledging != null}
                                        retrying={retryingLine === line.billingSyncItemId}
                                        onRetry={handleRetryLine}
                                        onAcknowledge={handleAcknowledge}
                                        acknowledging={acknowledging === line.billingSyncItemId}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
                </>
                )}
            </div>
        </div>
    );
}

/**
 * One line.
 *
 * The "what to do" column is the point of this screen. A reason alone is diagnosis; naming the tab
 * that fixes it is what the requirement means by "and resolve".
 */
function StatusRow({
    line, disabled, retrying, onRetry, onAcknowledge, acknowledging,
}) {
    const state = describeState(line);
    const resolution = describeResolution(line.lastError);
    const next = describeNextAttempt(line);
    const acknowledged = line.itemStatus === "ACKNOWLEDGED";

    // Muted when it is only here because the reseller asked to see what they had hidden.
    return (
        <tr className={acknowledged ? "text-muted" : undefined}>
            <td>{line.customerLabel}</td>
            <td>{describeAdditionType(line.additionType)}</td>
            <td>{line.periodMonth}/{line.periodYear}</td>
            <td>
                <span className={`badge bg-${state.tone}`}>{state.label}</span>
                {line.attemptCount > 1 && (
                    <div className="text-muted" style={{ fontSize: 12 }}>
                        {line.attemptCount} attempts
                    </div>
                )}
            </td>
            <td style={{ maxWidth: 420 }}>
                {resolution ? (
                    <div style={{ fontSize: 13 }}>{resolution.action}</div>
                ) : line.lastError ? (
                    // No destination recognised — the reason alone, rather than a guess at a tab.
                    <div className="text-muted" style={{ fontSize: 13 }}>{line.lastError}</div>
                ) : next ? (
                    <div className="text-muted" style={{ fontSize: 13 }}>{next}</div>
                ) : (
                    <span className="text-muted">—</span>
                )}
                {resolution && next && (
                    <div className="text-muted" style={{ fontSize: 12 }}>{next}</div>
                )}
            </td>
            <td className="text-end">
                {line.canRetry && (
                    <button
                        type="button"
                        className="btn btn-sm btn-primary me-2"
                        disabled={disabled}
                        onClick={() => onRetry(line.billingSyncItemId)}
                    >
                        {retrying ? "Retrying…" : "Retry"}
                    </button>
                )}
                {/* Outline, not solid: this is the quieter of the two actions on the row, and the
                    one that should never be reached for first. */}
                {line.canAcknowledge && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={disabled}
                        title="Hide this failure. Does not cancel anything already sent."
                        onClick={() => onAcknowledge(line.billingSyncItemId)}
                    >
                        {acknowledging ? "Hiding…" : "Acknowledge"}
                    </button>
                )}
            </td>
        </tr>
    );
}
